import { Lambda, observe, action, observable, extendObservable, toJS } from "mobx";
import { internalMessageBus, InternalMessageBusMessage, InternalMessageBusChannels, EventsBySportIdRequest, UserInfoCurrent } from "sb-resp-lib";
import * as i from "../contracts";
import { ObservableData, ChannelListeners, FilterPredicate, SubFilterPredicate, DateRangeParam, SportsFilterMessage, DateFilterMessage, OddStyleMessage, TimeZoneMessage, AddOddDataType, BetSlipItemsMessage, OpenGameDataType, LeaguesConfiguration, LeaguesFilterMessage, EventCardMessage, Loader, NavigationFunctions, CardConfiguration } from "../typings";
import { EmptyStatisticsData, StreamingFilterBlockConstant } from "../constants";
import { DateRangeMapper, mapStartDateTime, mapMarketsSelectionsOdds, prepareMarkets, getMarketId, getBetSlipOdds, getSelectionSide, isMarketQA, getMarketTypeId, mapLinkUrl, getOutrightMarketIds, mapLeagueUrl, mapIsSelected, mapSelectionsOdds, mapEventComponent } from "../helpers";
import { Retriever } from "../Retriever";
import * as contacts from "../contracts";
import { Piper } from "../piper";
import { mapKickOffTime } from "../composables/mappers";
import { sortLeagues } from "../composables/sorters";
import Debounce from "debounce-decorator";
import { FavoritesManager } from "../helpers/FavoritesManager";

export type RequestParams = {
    sportIds: string[],
    allSportIds: string[],
    dateRange?: DateRangeParam
}

export class Parser {
    private piper: Piper;
    private eventsDisposers: Lambda[];
    private rootDisposers: Lambda[];
    private retriever: Retriever;
    private topicPrefix: string;
    private listeners: ChannelListeners;
    private globalListeners: ChannelListeners;
    private betSlipUtil: BetSlipUtil;
    private clientId: string;
    private favoritesManager: FavoritesManager;
    private allSportIds: string[];
    private mapEventLinkUrl: Function;
    private mapLeagueLinkUrl: Function;
    private specialLeagues: LeaguesConfiguration;
    private outrightMarketIds: string[];
    private dateFormat: string;
    private timeFormat: string;
    @observable public data: ObservableData;

    @observable sports: string[];
    @observable dateRange?: DateRangeParam;
    @observable public oddStyle: number;
    @observable public timeZone: number;
    @observable isInFavoritesState: boolean;
    @observable isMiniEventCard: boolean;
    @observable loader: Loader;
    isFavoritesEnabled: boolean;
    activeEventId: string;

    constructor(retriever: Retriever, piper: Piper, betSlipUtil: BetSlipUtil, topicPrefix: string, clientId: string, requstParams: RequestParams, navFunctions: NavigationFunctions, specialLeagues: LeaguesConfiguration) {
        this.piper = piper;
        this.retriever = retriever;
        this.betSlipUtil = betSlipUtil;
        this.topicPrefix = `${topicPrefix}.`;
        this.clientId = clientId;
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
        this.eventsDisposers = [];
        this.rootDisposers = [];
        this.sports = requstParams.sportIds;
        this.dateRange = requstParams.dateRange;
        this.allSportIds = requstParams.allSportIds;
        this.isFavoritesEnabled = retriever.dataProvider.isFavoritesEnabled;
        this.isMiniEventCard = retriever.dataProvider.isMiniEventCard;
        this.loader = retriever.loader;
        this.isInFavoritesState = false;
        this.dateFormat = retriever.dataProvider.shortDateFormat;
        this.timeFormat = retriever.dataProvider.shortTimeFormat;
        this.activeEventId = "";
        this.onLogin = this.onLogin.bind(this);
        this.favoritesManager = new FavoritesManager(`favoriteEvents_${topicPrefix}`);
        this.oddStyle = 0;
        this.timeZone = 0;
        this.mapEventLinkUrl = mapLinkUrl.bind(this, navFunctions.adjustLivePath, navFunctions.adjustPath);
        this.mapLeagueLinkUrl = mapLeagueUrl.bind(this, navFunctions.adjustPath);
        this.specialLeagues = specialLeagues;
        this.outrightMarketIds = getOutrightMarketIds(retriever.dataProvider.cardConfiguration);

        this.globalListeners = {};
        this.globalListeners[InternalMessageBusChannels.legacy.timeZone.update] = "timeZoneChange";
        this.globalListeners[InternalMessageBusChannels.legacy.oddStyle.update] = "oddStyleChange";
        this.globalListeners[InternalMessageBusChannels.legacy.userInfo.onLogin] = "onLogin";
        this.globalListeners[InternalMessageBusChannels.legacy.userInfo.onLogout] = "onLogout";
        this.globalListeners[InternalMessageBusChannels.legacy.platformJWTChange.renewJWTToken] = "renewJWTToken";
        this.globalListeners[InternalMessageBusChannels.legacy.betslip.items.add] = "betSlipItemsAdd";
        this.globalListeners[InternalMessageBusChannels.legacy.betslip.items.remove] = "betSlipItemsRemove";

        this.listeners = {};
        this.listeners[InternalMessageBusChannels.legacy.eventCard.changedToSide] = "changedToSide";
        this.listeners[InternalMessageBusChannels.legacy.filter.events.none] = "allEventsChange";
        this.listeners[InternalMessageBusChannels.legacy.filter.events.liveStream] = "liveStreamingChange";
        this.listeners[InternalMessageBusChannels.legacy.filter.events.fastMaskets] = "fastMarketsChange";
        this.listeners[InternalMessageBusChannels.legacy.activeEvent.update] = "activeEventIdChange";

        Object.keys(InternalMessageBusChannels.eventsFilter).forEach(key => {
            this[`${key}Change`] && (this.listeners[InternalMessageBusChannels.eventsFilter[key]] = `${key}Change`);
        });

        this.piper.appendAll(
            ["mapGameScore", "mapGamePart", "mapEventIcons", "mapParticipants", "mapTeamNames", "mapStatistics", "mapKickOffState", "mapAggregatedEventsCount", "mapGameTime", "sortEventsByTime", "filterExpiredOutrights"]
        );

    }

    // TODO: observe only what is shown on the screen
    private attachEventObservables() {
        this.eventsDisposers = [
            ...this.retriever.data.events.map(x => observe(x, this.updateSingleEvent)),
            ...this.retriever.data.markets.map(x => observe(x, this.updateSingleMarkets)),
            ...this.retriever.data.leagues.map(x => observe(x, this.updateLeagues)),
        ];
    }

    private dettachEventObservables() {
        this.eventsDisposers.forEach(x => typeof x === "function" && x());
        this.eventsDisposers = [];
    }

    private attachAllObservables() {
        this.attachEventObservables();
        this.rootDisposers = [
            observe(this.retriever.data.events, this.updateSingleEvent),
            observe(this.retriever.data.markets, this.updateSingleMarkets),
            observe(this.retriever.data.leagues, this.updateLeagues),
            observe(this.piper.pipeline, this.updateSingleEvent),
            observe(this.retriever.loader, this.updateLoader)
        ];
    }
    private dettachAllObservables() {
        this.rootDisposers.forEach(x => typeof x === "function" && x());
        this.rootDisposers = [];
        this.dettachEventObservables();
    }

    public onLogin(user: UserInfoCurrent) {
        user && this.retriever.setRegionId(user.CountryOfRegistration);
    }

    public onLogout() {
        this.retriever.removeRegionId();
    }

    @Debounce 250
    public renewJWTToken() {
        this.retriever.renewJWTToken();
    }

    @action.bound public changedToSide(eventCardMessage: EventCardMessage) {
        this.isMiniEventCard = eventCardMessage.changedToSide;
        let cardConfig = this.getCardConfig(this.isMiniEventCard);

        if (!eventCardMessage.changedToSide) {
            this.retriever.subFilterPredicate = SubFilterPredicate.None;
            this.data.markets.replace(prepareMarkets(this.data.markets, cardConfig, this.specialLeagues, this.outrightMarketIds));
        }

        this.data.events.forEach(ev => {
            mapEventComponent(ev, cardConfig, this.isMiniEventCard);
        });

        this.retriever.dataProvider.isMiniEventCard = this.isMiniEventCard;
        if (eventCardMessage.initActiveEvent && !this.activeEventId && this.data.events.length) {
            const firstEvent = this.data.events[0];
            this.activeEventId = firstEvent.id;
            internalMessageBus.emit(`${this.topicPrefix}${InternalMessageBusChannels.legacy.activeEvent.init}`, {
                eventId: this.activeEventId,
                isOutright: firstEvent.type === EventsBySportIdRequest.EventTypesEnum.Outright.toString()
            });
        }
        this.sendTagsMessage(this.data.events);
    }

    private attachMsgBusRouter() {
        Object.keys(this.listeners).forEach(x => internalMessageBus.on(`${this.topicPrefix}${x}`, this.msgRouter, this));
        Object.keys(this.globalListeners).forEach(x => internalMessageBus.on(x, this.msgRouterGlobal, this));
    }
    private dettachMsgBusRouter() {
        Object.keys(this.listeners).forEach(x => internalMessageBus.off(`${this.topicPrefix}${x}`, this.msgRouter, this));
        Object.keys(this.globalListeners).forEach(x => internalMessageBus.off(x, this.msgRouterGlobal, this));
    }

    private msgRouter(msg: InternalMessageBusMessage<any>) {
        this[this.listeners[`${msg.channel}.${msg.subChannel.join(".")}`.replace(this.topicPrefix, "")]](...msg.data);
    }

    private msgRouterGlobal(msg: InternalMessageBusMessage<any>) {
        this[this.globalListeners[`${msg.channel}.${msg.subChannel.join(".")}`]](...msg.data);
    }

    private requestGlobalListeners() {
        internalMessageBus.emit(InternalMessageBusChannels.legacy.timeZone.request, { callback: this.timeZoneChange });
        internalMessageBus.emit(InternalMessageBusChannels.legacy.oddStyle.request, { callback: this.oddStyleChange });
        internalMessageBus.emit(InternalMessageBusChannels.legacy.userInfo.request, { callback: this.onLogin });
    }

    private updateEventsTimeZone(events) {
        this.data.events.replace(events.map((event) => mapStartDateTime(event, this.timeZone, this.dateFormat, this.timeFormat)));
    }

    managedEvents(events: i.Event[]): i.Event[] {
        let filteredEvents = this.filterEvents(events);
        let eventsToUpdate = this.mapEvents(this.piper.executePipeline(filteredEvents));
        return eventsToUpdate;
    }

    singleEventUpdate(change) {
        if (change.type === "update") {
            let events = this.managedEvents([toJS(change.newValue)]);
            events.forEach(ev => {
                let index = this.data.events.findIndex(e => e.id === ev.id);
                index > -1 && (this.data.events[index] = ev);
            });
        }
    }

    singleEventAddUpdate(added: contacts.Event[]) {
        if (added) {
            let addedEvents = this.managedEvents(added.slice().map(ev => ({ ...ev })));
            addedEvents.forEach(ev => this.data.events.push(ev));
        }
    }

    @action.bound updateSingleEvent(change: any) {
        let removed = change.removed,
            added = change.added;

        this.handleRemove(removed, "events");
        this.singleEventUpdate(change);
        this.singleEventAddUpdate(added);
    }

    @action.bound manageFavorites(event: i.Event) {
        event.isInFavorites = !event.isInFavorites;
        this.favoritesManager.manageFavorites(event);
    }

    @action sportsChange(x: SportsFilterMessage) {
        this.isInFavoritesState = false;
        this.sports = x.sportIds;
        this.setSubFilterPredicate(SubFilterPredicate.None);
        this.updateCurrentSportsData();
        this.retriever.querySportUpdate(x.sportIds);
    }

    @action leaguesChange(x: LeaguesFilterMessage) {
        this.isInFavoritesState = false;
        this.sports = [x.sportId];
        this.retriever.queryLeaguesUpdate(x.leagueIds, this.sports);
    }

    @action datesChange(x: DateFilterMessage) {
        this.isInFavoritesState = false;
        this.dateRange = x.dateRange;
        this.updateCurrentDatesData();
        this.retriever.queryDatesUpdate(this.dateRange, this.sports);
    }

    @action liveStreamChange() {
        if (this.isCurrentFilter(FilterPredicate.LiveStreaming)) {
            return;
        }

        this.isInFavoritesState = false;
        this.sports = this.allSportIds;
        this.dateRange = undefined;
        this.updateCurrentTagsData(EventsBySportIdRequest.EventTagsEnum.LiveStreaming);
        this.retriever.queryTagsUpdate(EventsBySportIdRequest.EventTagsEnum.LiveStreaming);
    }

    @action favoritesChange() {
        if (this.isCurrentFilter(FilterPredicate.Favorites)) {
            return;
        }
        this.isInFavoritesState = true;
        this.sports = this.favoritesManager.favoriteSportIds;
        this.dateRange = undefined;
        this.retriever.queryFavoritesUpdate(this.favoritesManager.getFavorites);
    }

    @action liveStreamingChange() {
        this.setSubFilterPredicate(SubFilterPredicate.LiveStreaming);
    }

    @action fastMarketsChange() {
        this.setSubFilterPredicate(SubFilterPredicate.FastMarket);
    }

    @action allEventsChange() {
        this.setSubFilterPredicate(SubFilterPredicate.None);
    }

    @action activeEventIdChange(eventId: string) {
        if (!eventId) {
            return;
        }

        this.activeEventId = eventId;
        this.updateEvents();
    }

    @action activate() {
        this.retriever.activate();
        this.attachMsgBusRouter();
        this.attachAllObservables();
        this.isFavoritesEnabled && this.favoritesManager.loadFavorites()
        this.requestGlobalListeners();
    }

    @action deactivate() {
        this.dettachAllObservables();
        this.dettachMsgBusRouter();
        this.retriever.deactivate();
        this.clearData();
    }

    setParams(sportIds: string[]) {
        this.sports = sportIds;
        this.retriever.setParams(sportIds);
    }

    handleRemove(collection: contacts.Event[] | contacts.Market[], type: string) {
        if (collection) {
            collection.forEach(el => {
                this.data[type].splice(this.data[type].findIndex(x => x.id === el.id), 1);
            })
        }
    }

    @action.bound private updateEvents() {
        this.dettachEventObservables();
        let events = this.retriever.data.events.slice().map(ev => ({ ...ev }));
        let filteredEvents = this.filterEvents(events);
        this.data.events.replace(this.mapEvents(this.piper.executePipeline(filteredEvents)));
        this.attachEventObservables();
    }

    @action.bound private updateMarkets() {
        this.dettachEventObservables();
        let markets = this.retriever.data.markets.slice().map(m => {
            m.selections = m.selections.slice();
            m.tags = m.tags.slice();
            return { ...m };
        });

        let cardConfig = this.getCardConfig(this.isMiniEventCard);
        let marketsFiltered = prepareMarkets(markets, cardConfig, this.specialLeagues, this.outrightMarketIds);
        this.data.markets.replace(this.mapMarkets(marketsFiltered));
        this.attachEventObservables();
    }

    @action.bound private updateSingleMarkets(change: any) {
        this.dettachEventObservables();
        let removed = change.removed,
            added = change.added,
            cardConfig = this.getCardConfig(this.isMiniEventCard);

        this.handleRemove(removed, "markets");
        this.batchedUpdateSingleMarkets(change, cardConfig);
        this.addedMarkets(added, cardConfig);
    }

    batchedUpdateSingleMarkets(change: any, cardConfig: CardConfiguration) {
        if (change.type === "update") {
            let markets = prepareMarkets([toJS(change.newValue)], cardConfig, this.specialLeagues, this.outrightMarketIds);
            let filteredMarkets = this.mapMarkets(markets);
            filteredMarkets.forEach(market => {
                let index = this.data.markets.findIndex(e => e.id === market.id);
                index > -1 && (this.data.markets[index] = market);
            });
        }
    }

    addedMarkets(added: contacts.Market[], cardConfig: CardConfiguration) {
        if (added) {
            let addedMarkets = prepareMarkets(added.slice().map(market => ({ ...market })),
                cardConfig, this.specialLeagues, this.outrightMarketIds);
            addedMarkets = this.mapMarkets(addedMarkets);
            addedMarkets.forEach(market => this.data.markets.push(market));
        }
    }

    @action.bound private updateLoader() {
        this.loader = this.retriever.loader;
        if (!this.retriever.loader.isLoading && this.retriever.loader.isFullLoadFinished) {
            const filteredEvents = this.filterEvents(this.retriever.data.events.slice());
            this.sendEventsCountMessage(filteredEvents.length);
            this.sendTagsMessage(filteredEvents);
        }
    }

    @action.bound private updateLeagues() {
        this.dettachEventObservables();
        let leagues = this.retriever.data.leagues.slice();
        let regions = this.retriever.activeRequest.request.regionIds;
        this.retriever.activeRequest.request.regionIds
        let topLeagues = leagues.filter(league => league.isTopLeague ||
            regions && regions.indexOf(league.regionId) >= 0);
        let notTopLeagues = leagues.filter(league => !(league.isTopLeague ||
            (regions && regions.indexOf(league.regionId) >= 0)));

        this.data.leagues.replace(topLeagues.sort(sortLeagues).concat(
            notTopLeagues.sort(sortLeagues)));
        this.mapLeagues();
        this.attachEventObservables();
    }

    public isCurrentFilter(newFilter: FilterPredicate): boolean {
        return this.retriever.filterPredicate === newFilter;
    }

    private isCurrentSubFilter(newFilter: SubFilterPredicate): boolean {
        return this.retriever.subFilterPredicate === newFilter;
    }

    private setSubFilterPredicate(newFilter: SubFilterPredicate) {
        if (this.isCurrentSubFilter(newFilter)) {
            return;
        }

        this.isInFavoritesState = false;
        this.dateRange = undefined;
        this.retriever.subFilterPredicate = newFilter;
        this.updateEvents();
    }

    @action.bound oddStyleChange(oddStyleMsg: OddStyleMessage) {
        this.oddStyle = oddStyleMsg.currentSelectedOddStyle;

        this.dettachEventObservables();
        this.data.markets.replace(mapMarketsSelectionsOdds(this.data.markets, this.oddStyle));
        this.attachEventObservables();
    }

    @action.bound timeZoneChange(timeZoneMsg: TimeZoneMessage) {
        this.timeZone = timeZoneMsg.currentSelectedTimeZone;

        this.dettachEventObservables();
        this.updateEventsTimeZone(this.data.events);
        this.attachEventObservables();
    }

    private sendEventsCountMessage(count: number) {
        let sport = this.retriever.filterPredicate === FilterPredicate.LiveStreaming ?
            StreamingFilterBlockConstant : this.sports[0];
        internalMessageBus.emit(`${this.topicPrefix}${InternalMessageBusChannels.eventsList.count}`,
            { eventsCount: count, sportId: sport });
    }

    sendTagsMessage(events: i.Event[]) {
        this.processTagsMessage(events);
    }

    processTagsMessage(events: i.Event[]) {
        let hasLiveStreamEvents = false;
        let hasFastMarketsEvents = false;

        for (let i = 0; i < events.length; i += 1) {
            const ev = events[i];
            ev.tags.indexOf(EventsBySportIdRequest.EventTagsEnum.LiveStreaming.toString()) > -1 && (hasLiveStreamEvents = true);
            ev.tags.indexOf(EventsBySportIdRequest.EventTagsEnum.FastMarket.toString()) > -1 && (hasFastMarketsEvents = true);

            if (hasLiveStreamEvents && hasFastMarketsEvents) {
                break;
            }
        }

        const result = { hasLiveStreamEvents, hasFastMarketsEvents };
        internalMessageBus.emit(`${this.topicPrefix}${InternalMessageBusChannels.eventsList.tags}`, result);

        return result;
    }

    private filterEvents(events: i.Event[]) {
        let to = this.dateRange && this.dateRange.from ? DateRangeMapper[this.dateRange.timeRange](this.dateRange.from) : "";
        let ids = this.retriever.filterPredicate === FilterPredicate.Favorites ? this.favoritesManager.favoriteEventIds : this.sports;
        let filterFn = this.retriever.getCurrentFilter.bind(this.retriever, ids, this.dateRange && this.dateRange.from, to);
        let filteredEvents = events.filter(filterFn);
        return filteredEvents;
    }

    mapSelectionBetslipStatus(market: i.Market) {
        market.selections = market.selections.map(
            (selection) => {
                selection.isSelectionInBetslip === undefined
                    ? extendObservable(selection, {
                        isSelectionInBetslip: this.isSelectionInBetSlip(market, selection)
                    })
                    : selection.isSelectionInBetslip = this.isSelectionInBetSlip(market, selection);
                return selection;
            }
        );
    }

    private isSelectionInBetSlip(market: i.Market, selection: i.Selection): boolean {
        let oddData = this.getAddOddData(market, selection);
        return oddData
            ? this.betSlipUtil.isOddInSlip(
                oddData.masterEventID,
                oddData.eventID,
                oddData.lineID,
                oddData.lineGroupID,
                oddData.rowTypeID,
                oddData.lineTypeID,
                oddData.isLive,
                oddData.isQA
            )
            : false;
    }

    public getAddOddData(market: i.Market, selection: i.Selection): AddOddDataType | undefined {

        if (!market || !selection) { return; }
        const marketEventID = this.outrightMarketIds.indexOf(market.marketType.id) > -1 ? "0" : market.eventId;
        return {
            masterEventID: parseInt(marketEventID),
            eventID: parseInt(getMarketId(market)),
            lineID: parseInt(selection.metadata.idSBTech),
            odds: parseInt(getBetSlipOdds(selection.displayOdds)),
            lineGroupID: selection.group,
            lineTypeID: parseInt(selection.metadata.type),
            rowTypeID: getSelectionSide(selection),
            points: selection.points || 0,
            isLive: market.isLive,
            isQA: isMarketQA(market),
            eventTypeId: parseInt(getMarketTypeId(market)),
            leagueId: parseInt(market.leagueId),
            betId: 0,
            preventUpdating: false,
            isFavedTeam: false
        };
    }

    @action.bound betSlipItemsAdd(data: BetSlipItemsMessage) {
        let selection = this.findSelection(data.marketId, data.selectionId);
        selection && (selection.isSelectionInBetslip = true);
    }

    @action.bound betSlipItemsRemove(data: BetSlipItemsMessage) {
        let selection = this.findSelection(data.marketId, data.selectionId);
        selection && (selection.isSelectionInBetslip = false);
    }

    private findSelection(marketId: string, selectionId: string) {
        return this.data.markets
            .filter(market => market.id.indexOf(marketId) >= 0)
            .map(x => x.selections.slice())
            .reduce((a, b) => [...a, ...b], [])
            .find(selection => selection.metadata.idSBTech === selectionId);
    }

    private getCardConfig(isMiniCard: boolean): CardConfiguration {
        return isMiniCard
            ? this.retriever.dataProvider.cardConfigurationEventView
            : this.retriever.dataProvider.cardConfiguration;
    }

    getOpenGameData(event: i.Event, ev: Event): OpenGameDataType | undefined {
        if (!event) { return; }

        let eventTyprForGameBlock;

        if (event.isLive) {
            eventTyprForGameBlock = event.type === EventsBySportIdRequest.EventTypesEnum.Outright.toString() ? 199 : 1;
        }
        else {
            eventTyprForGameBlock = event.type === EventsBySportIdRequest.EventTypesEnum.Outright.toString() ? 99 : 0;
        }

        return {
            event: ev,
            id: parseInt(event.id),
            isLive: event.isLive,
            evTypeForGameBlock: eventTyprForGameBlock,
            shouldHideSidePanels: false,
            branchID: parseInt(event.sportId)
        }
    }

    openStats(statisticsEventId: string, e: Event) {
        let statData = this.getStatisticsData(statisticsEventId);
        this.retriever.dataProvider.statisticsProvider.openStats(statData, e);
    }

    openLiveStream(eventId: string) {
        internalMessageBus.emit(InternalMessageBusChannels.legacy.liveStream.update, parseInt(eventId));
    }

    getStatisticsData(statisticsEventId: string): StatisticsData {
        return {
            ...EmptyStatisticsData,
            betRadarData: {
                clientId: this.clientId,
                eventId: statisticsEventId
            }
        }
    }

    @action.bound private mapEvents(events: i.Event[]) {
        let cardConfig = this.getCardConfig(this.isMiniEventCard);
        return events.map(
            (ev) => {
                // add events missing properties
                mapKickOffTime(ev, this.timeZone);
                mapStartDateTime(ev, this.timeZone, this.dateFormat, this.timeFormat);
                mapIsSelected(ev, this.activeEventId);
                this.mapEventLinkUrl(ev);
                this.isFavoritesEnabled && (ev.isInFavorites = this.favoritesManager.isInFavorites(ev.id));
                mapEventComponent(ev, cardConfig, this.isMiniEventCard);
                return ev;
            });
    }

    @action.bound private mapMarkets(updatedMarkets: i.Market[]) {
        return updatedMarkets.map(m => {
            let sel = mapSelectionsOdds(m.selections, this.oddStyle)
            m.selections = sel;
            this.mapSelectionBetslipStatus(m);
            return m;
        })
    }

    @action.bound private mapLeagues() {
        this.data.leagues.map(
            (league) => this.mapLeagueLinkUrl(league)
        )
    }

    updateCurrentSportsData() {
        (!this.retriever.data.events.length || this.retriever.loadedData.containsSportIds(this.sports)) && this.updateData();
    }

    updateCurrentDatesData() {
        const date = this.dateRange && this.dateRange.from ? this.dateRange.from : new Date();
        this.retriever.loadedData.containsSportIdsAndDate(this.sports, date) && this.updateData();
    }

    updateCurrentTagsData(tag: EventsBySportIdRequest.EventTagsEnum) {
        this.retriever.loadedData.containsTag(tag) && this.updateData();
    }

    private clearData() {
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
    }

    private updateData() {
        this.updateEvents();
        this.updateMarkets();
        this.updateLeagues();
    }
}
